import { Alert, Button, Card, DatePicker, Descriptions, Grid, Popconfirm, Space, Table, Tag, Typography, App as AntApp } from "antd";
import { PlusOutlined, EditOutlined, CheckCircleOutlined, LockOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { ApiError, errorLines } from "../app/problems.ts";
import { AcademicYearDrawer } from "../components/AcademicYearDrawer.tsx";
import { NonSchoolDaysEditor } from "../components/NonSchoolDaysEditor.tsx";

type AcademicYear = components["schemas"]["AcademicYearDetails"];
type YearStatus = components["schemas"]["AcademicYearStatus"];

const statusColor: Record<YearStatus, string> = { Planned: "default", Active: "green", Closed: "red" };

export function AcademicYearsPage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const canManage = hasPermission("academic.manage");
  const canManageYears = hasPermission("academic.manageYears");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ open: boolean; year?: AcademicYear }>({ open: false });

  const years = useQuery({
    queryKey: ["academic", "years"],
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/years");
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  // Keep a selection: the active year by default, otherwise the first (most recent) one.
  useEffect(() => {
    if (!years.data || years.data.length === 0) return;
    if (selectedId && years.data.some((y) => y.id === selectedId)) return;
    setSelectedId((years.data.find((y) => y.status === "Active") ?? years.data[0])?.id ?? null);
  }, [years.data, selectedId]);

  const selected = years.data?.find((y) => y.id === selectedId);

  const transition = useMutation({
    mutationFn: async ({ year, action }: { year: AcademicYear; action: "activate" | "close" }) => {
      const result =
        action === "activate"
          ? await api.POST("/academic/years/{id}/activate", { params: { path: { id: year.id } } })
          : await api.POST("/academic/years/{id}/close", { params: { path: { id: year.id } } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return { year: result.data, action };
    },
    onSuccess: async ({ year, action }) => {
      await queryClient.invalidateQueries({ queryKey: ["academic", "years"] });
      void message.success(t(action === "activate" ? "academicYears.activated" : "academicYears.closed", { label: year.label }));
    },
    onError: (err) => void message.error(errorLines(err, t("app.error")).join(" ")),
  });

  const format = (date: string) => dayjs(date).format("DD.MM.YYYY");

  const columns = [
    { title: t("academicYears.label"), dataIndex: "label", key: "label", ellipsis: true },
    { title: t("academicYears.startDate"), dataIndex: "startDate", key: "startDate", width: 120, render: format, responsive: ["sm" as const] },
    { title: t("academicYears.endDate"), dataIndex: "endDate", key: "endDate", width: 120, render: format, responsive: ["sm" as const] },
    {
      title: t("academicYears.status"),
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status: YearStatus) => <Tag color={statusColor[status]}>{t(`academicYears.statuses.${status}`)}</Tag>,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("academicYears.title")}
        </Typography.Title>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true })}>
            {t("academicYears.create")}
          </Button>
        )}
      </div>
      {years.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void years.refetch()}>{t("app.retry")}</Button>} />}
      <Table<AcademicYear>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={years.isPending}
        columns={columns}
        dataSource={years.data ?? []}
        pagination={false}
        locale={{ emptyText: t("academicYears.empty") }}
        onRow={(year) => ({ onClick: () => setSelectedId(year.id), style: { cursor: "pointer" } })}
        rowSelection={{ type: "radio", selectedRowKeys: selected ? [selected.id] : [], onChange: (keys) => setSelectedId(String(keys[0])) }}
      />
      {selected && (
        <Card
          title={selected.label}
          size="small"
          extra={
            <Space wrap>
              {canManage && selected.status !== "Closed" && (
                <Button icon={<EditOutlined />} onClick={() => setDrawer({ open: true, year: selected })}>
                  {t("common.edit")}
                </Button>
              )}
              {canManageYears && selected.status === "Planned" && (
                <Popconfirm
                  title={t("academicYears.activateConfirm", { label: selected.label })}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                  onConfirm={() => transition.mutate({ year: selected, action: "activate" })}
                >
                  <Button type="primary" icon={<CheckCircleOutlined />} loading={transition.isPending}>
                    {t("academicYears.activate")}
                  </Button>
                </Popconfirm>
              )}
              {canManageYears && selected.status === "Active" && (
                <Popconfirm
                  title={t("academicYears.closeConfirm", { label: selected.label })}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                  onConfirm={() => transition.mutate({ year: selected, action: "close" })}
                >
                  <Button danger icon={<LockOutlined />} loading={transition.isPending}>
                    {t("academicYears.closeYear")}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          }
        >
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Descriptions
              size="small"
              column={screens.md ? 3 : 1}
              items={[
                { key: "dates", label: t("academicYears.nonSchoolDays.dates"), children: `${format(selected.startDate)} – ${format(selected.endDate)}` },
                { key: "dayOff", label: t("academicYears.weeklyDayOff"), children: t(`academicYears.days.${selected.weeklyDayOff}`) },
                { key: "status", label: t("academicYears.status"), children: t(`academicYears.statuses.${selected.status}`) },
              ]}
            />
            <NonSchoolDaysEditor year={selected} canManage={canManage} />
            <SchoolDayCheck />
          </Space>
        </Card>
      )}
      <AcademicYearDrawer
        open={drawer.open}
        year={drawer.year}
        onClose={() => setDrawer({ open: false })}
        onSaved={(year, created) => {
          setDrawer({ open: false });
          setSelectedId(year.id);
          void message.success(t(created ? "academicYears.created" : "academicYears.updated", { label: year.label }));
        }}
      />
    </Space>
  );
}

/** Asks the API whether a chosen date is a school day; shows the status and the reason. */
function SchoolDayCheck() {
  const { t } = useTranslation();
  const [date, setDate] = useState<Dayjs | null>(null);
  const iso = date?.format("YYYY-MM-DD");

  const check = useQuery({
    queryKey: ["academic", "is-school-day", iso],
    enabled: !!iso,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/calendar/is-school-day", { params: { query: { date: iso! } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  return (
    <Card size="small" title={t("academicYears.calendar.title")}>
      <Space wrap>
        <DatePicker format="DD.MM.YYYY" placeholder={t("academicYears.calendar.pick")} aria-label={t("academicYears.calendar.pick")} onChange={setDate} />
        {check.data && (
          <Tag color={check.data.isSchoolDay ? "green" : "orange"} style={{ fontSize: 14, padding: "4px 10px" }}>
            {t(`academicYears.calendar.statuses.${check.data.status}`)}
            {check.data.reason ? `: ${check.data.reason}` : ""}
          </Tag>
        )}
      </Space>
    </Card>
  );
}
